import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState({ payment_status: "pending", code: null });
  const [tries, setTries] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      setTries(attempts);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid" && data.code) {
          // navigate to the general success page with the code display
          setTimeout(() => navigate(`/success/${data.order_id || orderId}`, { replace: true }), 800);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setFailed(true);
          return;
        }
      } catch (err) {
        // Ignore transient network errors between polls; log for observability.
        console.warn("PaymentSuccess poll transient error:", err?.message || err);
      }
      if (attempts < 30) {
        setTimeout(poll, 2000);
      } else {
        setFailed(true);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, orderId, navigate]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white relative">
      <BrandHeader />
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" aria-hidden />
      <main className="relative z-10 max-w-2xl mx-auto px-6 py-24 text-center">
        {failed ? (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-display font-black text-4xl mb-3">Pago no confirmado</h1>
            <p className="text-zinc-400 mb-8">
              No pudimos verificar tu pago. Si el cargo se realizó, contáctanos y validaremos manualmente.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
              data-testid="back-home-btn"
            >
              Volver al inicio
            </Button>
          </>
        ) : (
          <>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500 mb-8"
            >
              {status.payment_status === "paid" ? (
                <CheckCircle2 className="w-10 h-10 text-emerald-400" />
              ) : (
                <Loader2 className="w-10 h-10 text-yellow-400 animate-spin" />
              )}
            </motion.div>
            <div className="text-xs uppercase tracking-[0.35em] text-yellow-400 font-mono-display mb-4">
              {status.payment_status === "paid" ? "Pago confirmado" : "Verificando pago..."}
            </div>
            <h1 className="font-display font-black text-4xl mb-3" data-testid="payment-status-title">
              {status.payment_status === "paid"
                ? "¡Gracias por tu pago!"
                : "Confirmando con Stripe"}
            </h1>
            <p className="text-zinc-400 mb-8">
              {status.payment_status === "paid"
                ? "Redirigiendo a tu código de despacho..."
                : `Intento ${tries}/30 — esto suele tomar solo unos segundos.`}
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
